/**
 * Semantic mapping of FROZEN backend ErrorEnvelope codes → frontend UI state.
 *
 * The codes are the ones declared in `app/errors.py`. NEVER invent new
 * strings. This module is a pure lookup — no I/O, no side effects.
 */

export type SaleErrorCategory =
  | "geometry_invalid"
  | "mixed_zone"
  | "grid_zero_locked"
  | "reserve_locked"
  | "vip_cap_exceeded"
  | "rpc_unavailable"
  | "chain_response_malformed"
  | "chain_config_mismatch"
  | "sale_not_configured"
  | "sale_configuration_error"
  | "canonical_invalid"
  | "page_size_invalid"
  | "page_size_too_large"
  | "generation_invalid"
  | "cursor_invalid"
  | "buyer_invalid"
  | "parcel_id_invalid"
  | "sale_v2_not_ready"
  | "unknown";

// Every code below MUST match a real error constant in `backend/app/errors.py`.
const SALE_ERROR_MAP: Record<string, SaleErrorCategory> = {
  GEOMETRY_INVALID: "geometry_invalid",
  MIXED_ZONE: "mixed_zone",
  LOCKED_GRID_ZERO: "grid_zero_locked",
  LOCKED_RESERVE: "reserve_locked",
  VIP_CAP_EXCEEDED: "vip_cap_exceeded",
  SALE_RPC_UNAVAILABLE: "rpc_unavailable",
  SALE_V2_CHAIN_RESPONSE_MALFORMED: "chain_response_malformed",
  SALE_CHAIN_CONFIG_MISMATCH: "chain_config_mismatch",
  SALE_NOT_CONFIGURED: "sale_not_configured",
  SALE_CONFIGURATION_ERROR: "sale_configuration_error",
  CANONICAL_INVALID: "canonical_invalid",
  PAGE_SIZE_INVALID: "page_size_invalid",
  PAGE_SIZE_TOO_LARGE: "page_size_too_large",
  GENERATION_INVALID: "generation_invalid",
  CURSOR_INVALID: "cursor_invalid",
  BUYER_INVALID: "buyer_invalid",
  PARCEL_ID_INVALID: "parcel_id_invalid",
  SALE_V2_NOT_READY: "sale_v2_not_ready",
};

/**
 * Map a backend ErrorEnvelope `code` → `SaleErrorCategory`.
 * Unknown/absent code → "unknown" (never throws).
 *
 * Some V2 quote errors surface via `details.reason` when the outer
 * code is the shared GEOMETRY_INVALID. Handle that below.
 */
export function mapSaleErrorCategory(
  code: string | undefined,
  details?: Record<string, unknown> | null,
): SaleErrorCategory {
  if (!code) return "unknown";
  // V2 quote surfaces zone-mixed / grid-zero / reserve via a shared
  // outer 422 code plus `details.reason`. If present, that wins.
  if (details && typeof details === "object" && "reason" in details) {
    const reason = String((details as { reason?: unknown }).reason || "");
    if (reason === "mixed_zone") return "mixed_zone";
    if (reason === "locked_grid_zero" || reason === "grid_zero_locked")
      return "grid_zero_locked";
    if (reason === "locked_reserve" || reason === "reserve_locked")
      return "reserve_locked";
  }
  return SALE_ERROR_MAP[code] ?? "unknown";
}

/**
 * Human-readable (technical) label for a category. UI copy layer can
 * translate/localize on top; this stays neutral and stable.
 */
export function labelForSaleErrorCategory(cat: SaleErrorCategory): string {
  switch (cat) {
    case "geometry_invalid": return "Selection geometry is invalid.";
    case "mixed_zone": return "Selection spans multiple zones.";
    case "grid_zero_locked": return "Grid Zero is locked (not sellable).";
    case "reserve_locked": return "Reserve area is locked (not sellable).";
    case "vip_cap_exceeded": return "VIP parcel exceeds the per-parcel cap.";
    case "rpc_unavailable": return "On-chain RPC temporarily unavailable.";
    case "chain_response_malformed":
      return "On-chain response is malformed; try again later.";
    case "chain_config_mismatch":
      return "Sale chain configuration mismatch.";
    case "sale_not_configured": return "Sale subsystem is not configured.";
    case "sale_configuration_error":
      return "Sale subsystem configuration error.";
    case "canonical_invalid": return "Invalid `canonical` filter value.";
    case "page_size_invalid": return "Invalid `page_size` value.";
    case "page_size_too_large": return "`page_size` above the server cap.";
    case "generation_invalid": return "Invalid `generation` value.";
    case "cursor_invalid": return "Cursor no longer valid — reload the page.";
    case "buyer_invalid": return "Invalid buyer address.";
    case "parcel_id_invalid": return "Invalid parcel id.";
    case "sale_v2_not_ready": return "SaleV2 read path not ready yet.";
    case "unknown": return "Unexpected error.";
  }
}
